import React, { useState } from 'react';

import ClockIcon from 'icons/clock.svg';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Tabs from 'components/application/Tabs';

import PersonnelRoleTrainingStatusIndicator from 'components/training/PersonnelRoleTrainingStatusIndicator';
import RoleTrainingTab from 'components/training/RoleTrainingTab';
import TrainingModalBox from 'components/training/TrainingModalBox';
import TrainingEventsTab from 'components/trainingEvents/TrainingEventsTab';

import { personnelCourseConnectionsRequirements } from 'components/helpers/resources/courses';
import {
  getTrainingStatus,
  trainingStatusReport,
  uniqueTrainingCollectionByCourse,
} from 'components/helpers/resources/training';

const defaultModalBoxData = {
  attachments: [],
  context: 'notes',
  course: null,
  loaded: false,
  training: null,
};

export default function TrainingRecords({
  attachments,
  autoEnrolmentConfigurations,
  autoEnrolmentExclusions,
  bookers,
  courseCompanyRoles,
  courses,
  coursesMeta,
  creditInfoUnavailable,
  currentBookings,
  currentPersonnel,
  currentRegistrations,
  eLearningAllowance,
  eLearningCourses,
  hasPersonnelEventViewableAccess,
  hasTrainingEditableAccess,
  onAddBooking,
  onAddELearningBooking,
  onAddRole,
  onBookingReminderClick,
  onCourseSelectChange,
  onCourseSelectError,
  onCreateAutoEnrolmentExclusion,
  onDeleteAutoEnrolmentExclusion,
  onDeleteCourseClick,
  onDeleteTrainingClick,
  onEditTrainingClick,
  onNewTrainingClick,
  onPersonnelEventsPageChange,
  onRemoveBooking,
  onShowBooking,
  onTrainingEvidenceProcessed,
  onUserCourseCreate,
  onUserCourseUpdate,
  personnelCourseConnectionRequirementPriorities,
  personnelEventsPage,
  roles,
  simpleUsers,
  training,
  trainingEvidenceProcessed,
  userCourses,
}) {
  const [activeTab, setActiveTab] = useState(null);
  const [modalBoxData, setModalBoxData] = useState(defaultModalBoxData);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.TrainingRecords',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const isArchived = !!currentPersonnel.archivedAt;

  const individualCourseTrainingStatusIds = {
    missing: [],
    expiryDateMissing: [],
    expired: [],
    invalid: [],
    expiring: [],
    valid: [],
  };

  const uniqueTrainingCollection = uniqueTrainingCollectionByCourse({
    training,
  });

  const trainingsMap = new Map();
  uniqueTrainingCollection.forEach((training) => {
    trainingsMap.set(training.relationships.course.data.id, training);
  });

  const individuallyRequiredCourses = userCourses.reduce((acc, userCourse) => {
    if (userCourse.attributes.required) {
      const requiredCourse = courses[userCourse.relationships.course.data.id];
      const requiredTraining = trainingsMap.get(requiredCourse.id);
      const status = getTrainingStatus({
        training: requiredTraining,
        course: requiredCourse,
      });

      if (
        !individualCourseTrainingStatusIds[status].includes(requiredCourse.id)
      ) {
        individualCourseTrainingStatusIds[status].push(requiredCourse.id);
      }
      acc.push(requiredCourse);
    }
    return acc;
  }, []);

  const trainingTabs =
    roles.length === 0 ?
      [
        <RoleTrainingTab
          attachments={attachments}
          autoEnrolmentConfigurations={autoEnrolmentConfigurations}
          autoEnrolmentExclusions={autoEnrolmentExclusions}
          bookers={bookers}
          courseCompanyRoles={courseCompanyRoles}
          courses={courses}
          coursesMeta={coursesMeta}
          creditInfoUnavailable={creditInfoUnavailable}
          currentBookings={currentBookings}
          currentPersonnel={currentPersonnel}
          currentRegistrations={currentRegistrations}
          eLearningAllowance={eLearningAllowance}
          eLearningCourses={eLearningCourses}
          hasTrainingEditableAccess={hasTrainingEditableAccess}
          id='training'
          isArchived={isArchived}
          key='blankRoleTrainingTab'
          label={i18nTShared('training')}
          onAddBooking={onAddBooking}
          onAddELearningBooking={onAddELearningBooking}
          onAddRole={onAddRole}
          onBookingReminderClick={onBookingReminderClick}
          onCourseSelectChange={onCourseSelectChange}
          onCourseSelectError={onCourseSelectError}
          onCreateAutoEnrolmentExclusion={onCreateAutoEnrolmentExclusion}
          onDeleteAutoEnrolmentExclusion={onDeleteAutoEnrolmentExclusion}
          onDeleteCourseClick={onDeleteCourseClick}
          onDeleteTrainingClick={onDeleteTrainingClick}
          onEditTrainingClick={onEditTrainingClick}
          onNewTrainingClick={onNewTrainingClick}
          onRemoveBooking={onRemoveBooking}
          onShowBooking={onShowBooking}
          onTrainingEvidenceProcessed={onTrainingEvidenceProcessed}
          onUserCourseCreate={onUserCourseCreate}
          onUserCourseUpdate={onUserCourseUpdate}
          personnelCourseConnectionRequirementPriorities={
            personnelCourseConnectionRequirementPriorities
          }
          // eslint-disable-next-line jsx-a11y/aria-role
          role={null}
          roles={roles}
          setModalBoxData={setModalBoxData}
          simpleUsers={simpleUsers}
          training={training}
          trainingEvidenceProcessed={trainingEvidenceProcessed}
          trainingStatusReport={trainingStatusReport({
            courses,
            requiredCourses: individuallyRequiredCourses,
            courseTrainingStatusIds: individualCourseTrainingStatusIds,
            training,
          })}
          userCourses={userCourses}
        />,
      ]
    : roles.map((companyRole) => {
        // Clone the individualCourseTrainingStatusIds object to avoid copying over ids from the previous object when mapping over roles
        const requiredRoleCourseTrainingStatusIds = _.cloneDeep(
          individualCourseTrainingStatusIds,
        );
        const tabCompanyRoleCourses =
          companyRole.relationships.courses.data.map(
            (companyRoleCourse) => courses[companyRoleCourse.id],
          );
        const requiredRoleCourses = tabCompanyRoleCourses.reduce(
          (acc, course) => {
            const currentCcr = courseCompanyRoles.find(
              (ccr) =>
                ccr.relationships.companyRole.data.id === companyRole.id &&
                ccr.relationships.course.data.id === course.id,
            );

            const userCourse = userCourses.find(
              (uc) => uc.relationships.course.data.id === course.id,
            );

            const personnelCourseConnectionRequirementPriority =
              personnelCourseConnectionRequirementPriorities?.[
                userCourse?.relationships
                  .personnelCourseConnectionRequirementPriority.data?.id
              ];

            const { isRequired } = personnelCourseConnectionsRequirements({
              courseCompanyRole: currentCcr,
              isRequirementPriorityPresent:
                !!personnelCourseConnectionRequirementPriority,
              userCourse,
            });

            if (isRequired) {
              const requiredCourse =
                courses[currentCcr.relationships.course.data.id];
              const requiredTraining = trainingsMap.get(requiredCourse.id);
              const status = getTrainingStatus({
                training: requiredTraining,
                course: requiredCourse,
              });

              if (
                !requiredRoleCourseTrainingStatusIds[status].includes(
                  requiredCourse.id,
                )
              ) {
                requiredRoleCourseTrainingStatusIds[status].push(
                  requiredCourse.id,
                );
              }
              acc.push(requiredCourse);
            }
            return acc;
          },
          [],
        );

        const requiredCourses = [
          ...requiredRoleCourses,
          ...individuallyRequiredCourses,
        ];
        const roleTrainingStatusReport =
          isArchived ?
            { status: 'archived', courses: [] }
          : trainingStatusReport({
              companyRole,
              courseTrainingStatusIds: requiredRoleCourseTrainingStatusIds,
              courses,
              requiredCourses,
              training,
            });

        return (
          <div
            icon={
              roles.length > 1 ?
                <div className='tw-mr-1.5 tw-flex'>
                  <PersonnelRoleTrainingStatusIndicator
                    status={roleTrainingStatusReport.status}
                  />
                </div>
              : undefined
            }
            id={`companyRoleTraining--${companyRole.id}`}
            key={`companyRoleTrainingTab--${companyRole.id}`}
            label={
              roles.length === 1 ?
                i18nTShared('training')
              : companyRole.attributes.position
            }
          >
            <RoleTrainingTab
              attachments={attachments}
              autoEnrolmentConfigurations={autoEnrolmentConfigurations}
              autoEnrolmentExclusions={autoEnrolmentExclusions}
              bookers={bookers}
              courseCompanyRoles={courseCompanyRoles}
              courses={courses}
              coursesMeta={coursesMeta}
              creditInfoUnavailable={creditInfoUnavailable}
              currentBookings={currentBookings}
              currentPersonnel={currentPersonnel}
              currentRegistrations={currentRegistrations}
              eLearningAllowance={eLearningAllowance}
              eLearningCourses={eLearningCourses}
              hasTrainingEditableAccess={hasTrainingEditableAccess}
              isArchived={isArchived}
              onAddBooking={onAddBooking}
              onAddELearningBooking={onAddELearningBooking}
              onBookingReminderClick={onBookingReminderClick}
              onCourseSelectChange={onCourseSelectChange}
              onCourseSelectError={onCourseSelectError}
              onCreateAutoEnrolmentExclusion={onCreateAutoEnrolmentExclusion}
              onDeleteAutoEnrolmentExclusion={onDeleteAutoEnrolmentExclusion}
              onDeleteCourseClick={onDeleteCourseClick}
              onDeleteTrainingClick={onDeleteTrainingClick}
              onEditTrainingClick={onEditTrainingClick}
              onNewTrainingClick={onNewTrainingClick}
              onRemoveBooking={onRemoveBooking}
              onShowBooking={onShowBooking}
              onTrainingEvidenceProcessed={onTrainingEvidenceProcessed}
              onUserCourseCreate={onUserCourseCreate}
              onUserCourseUpdate={onUserCourseUpdate}
              personnelCourseConnectionRequirementPriorities={
                personnelCourseConnectionRequirementPriorities
              }
              role={companyRole}
              roles={roles}
              setModalBoxData={setModalBoxData}
              simpleUsers={simpleUsers}
              training={training}
              trainingEvidenceProcessed={trainingEvidenceProcessed}
              trainingStatusReport={roleTrainingStatusReport}
              userCourses={userCourses}
            />
          </div>
        );
      });

  if (hasPersonnelEventViewableAccess) {
    trainingTabs.push(
      <TrainingEventsTab
        icon={
          <span>
            <ClockIcon
              className='[&_path]:tw-fill-grey-500'
              height={16}
              width={16}
            />
          </span>
        }
        id='activity'
        key='personnelEventsTab'
        label={i18nT('trainingEventsTab.label')}
        onPageChange={onPersonnelEventsPageChange}
        page={personnelEventsPage}
        personnelId={currentPersonnel.id}
      />,
    );
  }

  return (
    <>
      <Tabs
        activeTab={activeTab}
        modifiers={['truncated']}
        onTabClick={setActiveTab}
      >
        {trainingTabs}
      </Tabs>
      <TrainingModalBox attachments={attachments} data={modalBoxData} />
    </>
  );
}

TrainingRecords.propTypes = {
  roles: PropTypes.array.isRequired,
  simpleUsers: PropTypes.object.isRequired,
  training: PropTypes.array.isRequired,
  bookers: PropTypes.object,
  currentPersonnel: PropTypes.object.isRequired,
  currentBookings: PropTypes.array,
  currentRegistrations: PropTypes.array,
  attachments: PropTypes.array,
  courses: PropTypes.object,
  autoEnrolmentConfigurations: PropTypes.object,
  autoEnrolmentExclusions: PropTypes.object,
  eLearningCourses: PropTypes.object,
  eLearningAllowance: PropTypes.object,
  courseCompanyRoles: PropTypes.array,
  onBookingReminderClick: PropTypes.func.isRequired,
  hasTrainingEditableAccess: PropTypes.bool,
  onShowBooking: PropTypes.func.isRequired,
  onRemoveBooking: PropTypes.func.isRequired,
  onCreateAutoEnrolmentExclusion: PropTypes.func,
  onDeleteAutoEnrolmentExclusion: PropTypes.func,
  onAddBooking: PropTypes.func.isRequired,
  onAddELearningBooking: PropTypes.func.isRequired,
  onUserCourseUpdate: PropTypes.func.isRequired,
  onUserCourseCreate: PropTypes.func.isRequired,
  onNewTrainingClick: PropTypes.func.isRequired,
  onEditTrainingClick: PropTypes.func.isRequired,
  onDeleteTrainingClick: PropTypes.func.isRequired,
  onDeleteCourseClick: PropTypes.func.isRequired,
  onCourseSelectChange: PropTypes.func.isRequired,
  onCourseSelectError: PropTypes.func.isRequired,
  onTrainingEvidenceProcessed: PropTypes.func.isRequired,
  personnelCourseConnectionRequirementPriorities: PropTypes.object,
  onPersonnelEventsPageChange: PropTypes.func.isRequired,
  personnelEventsPage: PropTypes.number.isRequired,
};

TrainingRecords.defaultProps = {
  currentBookings: [],
  currentRegistrations: [],
};
